/**
 * Habibi's Carbon shop assistant.
 *
 * No model, no API, no key, no quota. The matcher is pure string work and the
 * book is a static JSON asset, so this answers when Shopify is up and nothing
 * else has to be. Ported from the budget-autofix answer book.
 *
 * Grounding rule, which is the whole point of the thing: this file states no
 * facts. Every price, phone number, address and URL arrives in window.HC_ASSISTANT
 * from Liquid at render time (snippets/assistant.liquid). A price is only ever
 * the live product.price of the page you are standing on. A stale price in a
 * chat widget is a dispute at checkout, so the book is not allowed to hold one.
 *
 * Anything the book cannot confirm routes to the shop, and offers to email them
 * the conversation through Shopify's own contact form.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- matcher */

  // Interrogatives and filler carry no topic. "carbon" and "fiber" are in here
  // because on this store they appear in nearly every question and match
  // everything, which is the same as matching nothing.
  var STOP = {};
  ('a an the is are was were be been am i me my we our you your it its this that ' +
   'these tho thos to of in on at for with from by and or but if so as get got ' +
   'have has had do doe did can could will would should there here about any some ' +
   'just please hi hello hey thank thanks how what when why which who whose ' +
   'car vehicle need want know tell guy shop pleas carbon fiber fibre thing ' +
   'gui really actually actual thi where someone anyone else they them use'
  ).split(' ').forEach(function (w) { STOP[w] = 1; });

  /** Crude singulariser, so "wheels" matches "wheel" and "fobs" matches "fob". */
  function stem(w) {
    if (w.length > 3 && w.slice(-3) === 'ies') return w.slice(0, -3) + 'y';
    if (w.length > 3 && w.slice(-2) === 'es' && w.slice(-3) !== 'ses') return w.slice(0, -2);
    if (w.length > 3 && w.slice(-1) === 's' && w.slice(-2) !== 'ss') return w.slice(0, -1);
    return w;
  }

  function tokenize(text) {
    return String(text)
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, ' ')
      .split(/\s+/)
      .map(stem)
      .filter(function (t) { return t.length > 1 && !STOP[t]; });
  }

  /** How many of the query's own words this entry accounts for. */
  function covered(qTokens, entry) {
    // Single-word keywords define the key set; multi-word keywords are phrases
    // and ONLY match as phrases. Letting a phrase leak its tokens into the key
    // set is how "made to order" made the lead-time entry answer "how do I
    // order" - if a bare word should match, list it as a bare word.
    var keys = {};
    var phrases = [];
    entry.keywords.forEach(function (k) {
      if (k.indexOf(' ') > -1) { phrases.push(k); return; }
      tokenize(k).forEach(function (t) { keys[t] = 1; });
    });

    var joined = ' ' + qTokens.join(' ') + ' ';
    var n = 0;
    qTokens.forEach(function (t) { if (keys[t]) n += 1; });
    // A matching multi-word phrase is strong evidence - count it as one extra
    // word covered. A "phrase" that reduces to a single content token is NOT:
    // it is the same keyword counted twice, and that inflation let an entry
    // with more filler words in its list beat the entry that actually fit.
    phrases.forEach(function (p) {
      var pt = tokenize(p);
      if (pt.length < 2) return;
      if (joined.indexOf(' ' + pt.join(' ') + ' ') > -1) n += 1;
    });
    return n;
  }

  // Beyond this many content words, the question is too specific for a book to
  // answer safely - hand it to a human instead of guessing.
  var MAX_TOKENS = 8;
  var MIN_COVERAGE = 0.6;

  /**
   * Scoring is by COVERAGE of the question, not raw keyword hits, because
   * counting hits rewards entries with long keyword lists. A wrong canned
   * answer is worse than no answer: it confidently tells a customer something
   * about the wrong part. Anything short of a clear win falls through.
   */
  function matchEntry(query, entries) {
    var q = tokenize(query);
    if (!q.length || q.length > MAX_TOKENS) return null;

    var best = null;
    var bestScore = 0;
    entries.forEach(function (e) {
      var s = covered(q, e) / q.length;
      if (s > bestScore) { best = e; bestScore = s; }
    });
    return bestScore >= MIN_COVERAGE ? best : null;
  }

  /** Closest entries to a question the book could not answer confidently. */
  function nearest(query, entries, limit) {
    var q = tokenize(query);
    if (!q.length) return entries.slice(0, limit);
    var ranked = entries
      .map(function (e) { return { e: e, s: covered(q, e) }; })
      .filter(function (r) { return r.s > 0; })
      .sort(function (a, b) { return b.s - a.s; });
    return ranked.length
      ? ranked.slice(0, limit).map(function (r) { return r.e; })
      : entries.slice(0, limit);
  }

  // The matcher is the only part with logic worth breaking, so it is exported
  // for test-assistant.js. Node stops here; the browser carries on.
  if (typeof module === 'object' && module.exports) {
    module.exports = { tokenize: tokenize, covered: covered, matchEntry: matchEntry, nearest: nearest };
    return;
  }

  var cfg = window.HC_ASSISTANT;
  if (!cfg || document.getElementById('hc-assistant')) return;

  /* ------------------------------------------------------------------ facts */

  var facts = {};
  var entries = [];
  var ui = {};

  /** Replace {token} with a grounded fact. An unresolved token is a bug, so it
   *  is dropped rather than shown to a customer as literal braces. */
  function fill(text) {
    return String(text).replace(/\{(\w+)\}/g, function (m, key) {
      return facts[key] != null ? facts[key] : '';
    }).replace(/\s{2,}/g, ' ').trim();
  }

  /* --------------------------------------------------------------- transcript */

  var transcript = [];
  function log(role, text) { transcript.push(role + ': ' + text); }

  /* ------------------------------------------------------------------- view */

  var root, panel, feed, input, launcher, unread;
  var open = false;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;   // textContent, never innerHTML
    return n;
  }

  function scroll() { feed.scrollTop = feed.scrollHeight; }

  function bubble(role, text) {
    var b = el('div', 'hc-a-msg hc-a-' + role, text);
    feed.appendChild(b);
    scroll();
    return b;
  }

  function chips(items, onPick) {
    var wrap = el('div', 'hc-a-chips');
    items.forEach(function (label) {
      var b = el('button', 'hc-a-chip', label);
      b.type = 'button';
      b.addEventListener('click', function () { onPick(label); });
      wrap.appendChild(b);
    });
    feed.appendChild(wrap);
    scroll();
    return wrap;
  }

  function links(list) {
    var wrap = el('div', 'hc-a-links');
    var any = false;
    list.forEach(function (l) {
      var href = fill(l.url);
      if (!href) return;               // no URL from Liquid = no link shown
      var a = el('a', 'hc-a-link', l.label);
      a.href = href;
      wrap.appendChild(a);
      any = true;
    });
    if (any) { feed.appendChild(wrap); scroll(); }
  }

  /* ---------------------------------------------------------------- handoff */

  var handoffOpen = false;

  /**
   * The block route. Shopify's own contact form is the transport - a same-origin
   * POST that Shopify turns into an email to the store's customer service
   * address. No key, no server, nothing to leak.
   */
  function handoff(question) {
    if (handoffOpen) return;
    handoffOpen = true;

    var form = el('form', 'hc-a-handoff');
    var label = el('label', 'hc-a-handoff-label', 'Your email');
    var email = el('input', 'hc-a-input');
    email.type = 'email';
    email.required = true;
    email.placeholder = 'you@example.com';
    email.autocomplete = 'email';

    var note = el('input', 'hc-a-input');
    note.type = 'text';
    note.placeholder = 'Your car (year, make, model) - optional';

    var send = el('button', 'hc-a-send', 'Send this chat to the shop');
    send.type = 'submit';

    var status = el('div', 'hc-a-status');

    form.appendChild(label);
    form.appendChild(email);
    form.appendChild(note);
    form.appendChild(send);
    form.appendChild(status);
    feed.appendChild(form);
    scroll();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!email.value) return;
      send.disabled = true;
      status.textContent = 'Sending...';

      var shopForm = document.getElementById('hc-assistant-contact');
      if (!shopForm) { status.textContent = 'Please email ' + facts.email + ' instead.'; return; }

      var body =
        'Question the assistant could not answer:\n' + question + '\n\n' +
        (note.value ? 'Vehicle: ' + note.value + '\n\n' : '') +
        'Page: ' + window.location.href + '\n\n' +
        'Full conversation:\n' + transcript.join('\n');

      shopForm.querySelector('[name="contact[email]"]').value = email.value;
      shopForm.querySelector('[name="contact[body]"]').value = body;
      shopForm.querySelector('[name="contact[source]"]').value = 'Website chat assistant';

      fetch(shopForm.action, {
        method: 'POST',
        body: new FormData(shopForm),
        headers: { Accept: 'text/html' }
      })
        .then(function (res) {
          // Shopify redirects to ?contact_posted=true when it accepted the form.
          if (res.url && res.url.indexOf('contact_posted=true') > -1) return true;
          if (res.ok) return true;
          throw new Error('rejected');
        })
        .then(function () {
          form.remove();
          bubble('bot', 'Sent. The shop has your question and this conversation, and will get back to you at ' + email.value + '. If it is urgent, call ' + facts.phone + '.');
          handoffOpen = false;
        })
        .catch(function () {
          send.disabled = false;
          status.textContent = 'That did not go through. Please email ' + facts.email + ' or call ' + facts.phone + '.';
        });
    });
  }

  /* ------------------------------------------------------------------- ask */

  function answer(text) {
    var q = String(text).trim();
    if (!q) return;

    bubble('user', q);
    log('Visitor', q);
    input.value = '';

    var hit = matchEntry(q, entries);

    if (hit) {
      var a = fill(hit.a);
      bubble('bot', a);
      log('Assistant', a);
      if (hit.links) links(hit.links);
      // "ask" marks an answer the book deliberately would not confirm.
      if (hit.ask) handoff(q);
      return;
    }

    bubble('bot', ui.unknown);
    log('Assistant', ui.unknown);

    var near = nearest(q, entries, 3);
    if (near.length) {
      chips(near.map(function (e) { return e.q; }), function (label) { answer(label); });
    }
    handoff(q);
  }

  /* ------------------------------------------------------------------ mount */

  function toggle(force) {
    open = force != null ? force : !open;
    panel.hidden = !open;
    launcher.setAttribute('aria-expanded', String(open));
    if (open) {
      input.focus();
      if (unread) { unread.remove(); unread = null; }
    }
  }

  function build() {
    root = el('div', 'hc-a');
    root.id = 'hc-assistant';

    launcher = el('button', 'hc-a-launcher');
    launcher.type = 'button';
    launcher.setAttribute('aria-expanded', 'false');
    launcher.setAttribute('aria-label', 'Open the ' + ui.title + ' assistant');
    launcher.appendChild(el('span', 'hc-a-launcher-icon', '✉'));
    launcher.appendChild(el('span', 'hc-a-launcher-text', 'Ask us'));

    panel = el('div', 'hc-a-panel');
    panel.hidden = true;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', ui.title + ' assistant');

    var head = el('div', 'hc-a-head');
    var heading = el('div', 'hc-a-head-text');
    heading.appendChild(el('strong', null, ui.title));
    heading.appendChild(el('span', 'hc-a-sub', ui.subtitle));
    var close = el('button', 'hc-a-close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { toggle(false); });
    head.appendChild(heading);
    head.appendChild(close);

    feed = el('div', 'hc-a-feed');

    var bar = el('form', 'hc-a-bar');
    input = el('input', 'hc-a-input hc-a-ask');
    input.type = 'text';
    input.placeholder = ui.placeholder;
    input.setAttribute('aria-label', 'Ask a question');
    var go = el('button', 'hc-a-go', 'Ask');
    go.type = 'submit';
    bar.appendChild(input);
    bar.appendChild(go);
    bar.addEventListener('submit', function (e) { e.preventDefault(); answer(input.value); });

    panel.appendChild(head);
    panel.appendChild(feed);
    panel.appendChild(bar);
    root.appendChild(panel);
    root.appendChild(launcher);
    document.body.appendChild(root);

    launcher.addEventListener('click', function () { toggle(); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && open) toggle(false);
    });

    bubble('bot', ui.greeting);
    log('Assistant', ui.greeting);
    chips(ui.chips, function (label) { answer(label); });
  }

  /* ------------------------------------------------------------------- boot */

  fetch(cfg.book)
    .then(function (r) { return r.json(); })
    .then(function (book) {
      ui = book.ui;
      // Liquid facts win over the book's own, so a URL or price can never be
      // shadowed by something typed into the JSON.
      facts = Object.assign({}, book.facts, cfg.facts);
      entries = book.entries.filter(function (e) {
        // An entry needing a fact this page cannot supply (a live price off a
        // product page) is dropped rather than answered with a blank.
        return !e.requires || facts[e.requires];
      });
      build();
    })
    .catch(function () { /* no book, no widget - the site is unaffected */ });
})();
